import { format } from 'date-fns';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../query-client';
import { EnergyEventDto, EnergyUsageTimeseriesDto, IEnergyUsageRequest, IFetchSitesEnergyDataParams, IRequestEnergyUsageParams } from './interface';
import { API } from '../api';
import { DATE_FORMAT_CSV } from '../commonConst';
import { exportCSVData } from '../common/exportCSVData';

export const useSitesEnergyUsageForChartsQuery = (query: IFetchSitesEnergyDataParams, options = {}) => {
    return useQuery<EnergyUsageTimeseriesDto[], Error>({
        queryKey: [QueryKey.ENERGY_USAGES, query],
        queryFn: async () => {
            try {
                const { sans, date } = query;

                const response = await API.fetch(
                    `/energy/usage?${new URLSearchParams({
                        start: `${date.start.valueOf()}`,
                        end: `${date.end.valueOf()}`,
                        ...(sans && { sans: sans.join(',') }),
                        aggregate: 'true',
                        include: 'performance,events',
                    })}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const data = (await response.json()) as EnergyUsageTimeseriesDto[];
                return data;
            } catch (error: any) {
                throw new Error(error?.message || `Cannot get sites energy data!`);
            }
        },
        ...options,
    });
};

export const useSitesEventsForChartsQuery = (query: IFetchSitesEnergyDataParams, options = {}) => {
    return useQuery<EnergyEventDto[], Error>({
        queryKey: [QueryKey.ENERGY_USAGES_EVENTS, query],
        queryFn: async () => {
            try {
                const startDate = query.date.start;
                const endDate = query.date.end;

                const response = await API.fetch(
                    `/energy/events?${new URLSearchParams({
                        start: `${startDate.valueOf()}`,
                        end: `${endDate.valueOf()}`,
                        ...(query.siteIds && { siteIds: query.siteIds.join(',') }),
                        aggregate: 'true',
                    })}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const data = (await response.json()) as EnergyEventDto[];
                return data;
            } catch (error: any) {
                throw new Error(error?.message || `Cannot get sites events data!`);
            }
        },
        ...options,
    });
};

const createEnergyUsageRequest = async ({ source, enrollmentIds, date }: IRequestEnergyUsageParams, signal?: AbortSignal | null): Promise<void> => {
    const response = await API.fetch('/energy/usage/request', {
        ...(signal && { signal }),
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            start: date.start,
            end: date.end,
            source,
            enrollmentIds,
        }),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();

    throw new Error(respBody?.error?.message || `Cannot request energy usage data!`);
};

export const useCreateRequestEnergyUsageQuery = () => {
    return useMutation<void, Error, IRequestEnergyUsageParams>(createEnergyUsageRequest);
};

const getRecentEnergyUsageRequests = async (signal?: AbortSignal | null): Promise<IEnergyUsageRequest[]> => {
    const query = new URLSearchParams({
        limit: '10',
        offset: '0',
    });
    const response = await API.fetch(`/energy/usage/request?${query.toString()}`, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody?.data || [];
    }

    throw new Error(respBody?.error?.message || `Cannot request data!`);
};

export const useRecentEnergyUsageRequestsQuery = (options = {}) => {
    return useQuery<IEnergyUsageRequest[], Error>({
        queryKey: [QueryKey.ENERGY_USAGES_REQUESTS],
        queryFn: () => getRecentEnergyUsageRequests(),
        ...options,
    });
};

export const exportSitesEnergyUsageData = async ({ sans, date }: IFetchSitesEnergyDataParams): Promise<void> => {
    try {
        const response = await API.fetch(
            `/energy/usage?${new URLSearchParams({
                start: `${date.start.valueOf()}`,
                end: `${date.end.valueOf()}`,
                ...(sans && { sans: sans.join(',') }),
                aggregate: 'false',
                include: 'performance,market-prices',
                format: 'csv',
            })}`,
            {
                headers: {
                    'Content-Type': 'text/csv;charset=UTF-8',
                },
            }
        );

        if (!response.ok) {
            const respBody = await response.json();
            throw new Error(respBody?.error?.message || `Cannot export data!`);
        }

        const csvData = await response.text();

        const filename = `sites-energy-data--${format(date.start, DATE_FORMAT_CSV)}--${format(date.end, DATE_FORMAT_CSV)}.csv`;
        exportCSVData(csvData, filename);
    } catch (error: any) {
        throw new Error(error?.message || `Cannot export data!`);
    }
};

export const uploadEnergyFile = async (file: File, source: 'user-upload' | 'sharepoint-archive' | 'dom' | 'dom-archive') => {
    try {
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await API.fetch(`/data/inbox/upload?source=${source}`, {
            method: 'post',
            body: formData,
        });

        if (response.ok) {
            return;
        }

        await response.json();
    } catch (error: any) {
        throw new Error(error?.message || `Cannot upload file!`);
    }
};
